import { useMemo } from 'react';
import { isBloggerBusiness } from '../../../../../utils/accessService';
import { buildImageSource, buildImageSourceSet } from '../../../../../utils/buildImageSource';
import { useTranslation } from '../../../../../utils/useTranslation';
import { IconProduct } from '../../../../icon/IconProduct';
import { Flex } from '../../../../layout/Flex';
import { Image } from '../../../../media/Image';
import { Retry } from '../../../../media/Retry';
import { Typography } from '../../../../typography/Typography';
import { ProductMonetizationPlainText } from '../../../ProductMonetizationPlainText/ProductMonetizationPlainText';
import { ProductMediaCover, ProductTextContainer, ProductTitle } from '../../styles';
import { MAX_PRODUCTS_PICTURES } from './constants';
import { MultipleProductCardProps } from './types';

export const MultipleProductCard = ({ products, businessType }: MultipleProductCardProps) => {
    const t = useTranslation();

    const productsToView =
        products.items.length > MAX_PRODUCTS_PICTURES
            ? products.items.slice(0, MAX_PRODUCTS_PICTURES)
            : products.items;
    const productsToViewCount = productsToView.length;

    const hasMonetization = products.items.some((product) => product.monetizationEnabled);
    const hasUnavailable = products.items.some((product) => product.isUnavailable);

    const isBlogger = isBloggerBusiness(businessType);

    const productText = useMemo(() => {
        const unavailableLabel = hasUnavailable ? (
            <Typography
                variant={isBlogger ? 'caption2' : 'caption'}
                color={isBlogger ? 'error' : 'black'}
                textTransform="none"
                noWrap
                mt={0.25}
                weight={isBlogger ? 'medium' : 'regular'}
            >
                {t('Есть недоступные товары')}
            </Typography>
        ) : null;

        if (hasMonetization) {
            return (
                <>
                    {unavailableLabel}
                    <ProductMonetizationPlainText
                        color="white"
                        textTransform="none"
                        mt={unavailableLabel ? 0.25 : 0}
                    />
                </>
            );
        }

        return (
            unavailableLabel || (
                <Typography weight="medium" variant="caption" color="black" textTransform="none">
                    {t(products.description)}
                </Typography>
            )
        );
    }, [hasMonetization, hasUnavailable, isBlogger, products.description, t]);

    return (
        <>
            <>
                {productsToView.map((product, index) => {
                    const media = product.content[0]?.media;

                    return (
                        <ProductMediaCover
                            index={index}
                            key={index}
                            zIndex={productsToViewCount - index}
                        >
                            <Flex
                                alignItems="center"
                                justifyContent="center"
                                backgroundColor="white"
                            >
                                {media ? (
                                    <Retry>
                                        <Image
                                            width="100%"
                                            height="100%"
                                            src={buildImageSource(media)}
                                            srcSet={buildImageSourceSet(media)}
                                        />
                                    </Retry>
                                ) : (
                                    <IconProduct size={36} color="rgba(0, 0, 0, 0.12)" />
                                )}
                            </Flex>
                        </ProductMediaCover>
                    );
                })}
            </>
            <ProductTextContainer indexOffset={productsToView.length - 1}>
                <ProductTitle weight={hasUnavailable ? 'medium' : 'regular'}>
                    {t(products.title)}
                </ProductTitle>
                {productText}
            </ProductTextContainer>
        </>
    );
};
